import { PORTFOLIO_COLORS, normalized, text, to_array, unique_values } from './company_portfolio_common';
import {
  domain_badge_class,
  domain_name,
  is_active_case,
  is_high_risk,
  is_sla_at_risk
} from './company_portfolio_formatters';

function risk_label(active_count, high_risk_count) {
  const ratio = active_count ? high_risk_count / active_count : 0;
  if (ratio >= 0.2) return 'High';
  if (ratio >= 0.08) return 'Mid';
  return 'Low';
}

function pluralized(count, singular) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

function sla_percentage(active_count, at_risk_count) {
  if (!active_count) return 100;
  return Math.round(((active_count - at_risk_count) / active_count) * 100);
}

function project_identity(project) {
  return {
    id: Number(project.id || project.project_id),
    name: text(project.name || project.project_name || 'Unnamed scope')
  };
}

function domain_composition(domains = []) {
  return domains.slice(0, 4).map((label) => ({
    label,
    badge_class: domain_badge_class(label)
  }));
}

function entity_type_composition(entity_types = []) {
  return entity_types.slice(0, 4).map((entity_type) => ({
    label: entity_type,
    badge_class: domain_badge_class(entity_type)
  }));
}

function risk_payload(risk = '') {
  return { label: risk, badge_class: `is-${risk.toLowerCase()}` };
}

export function scope_from_project(project, records, roles, configs) {
  const active_records = records.filter(is_active_case);
  const at_risk_count = active_records.filter(is_sla_at_risk).length;
  const high_risk_count = active_records.filter(is_high_risk).length;
  const domains = unique_values([
    ...configs.map((config) => text(config.entity_type_name)),
    ...active_records.map(domain_name)
  ]);
  const risk = risk_label(active_records.length, high_risk_count);
  const sla = sla_percentage(active_records.length, at_risk_count);

  return {
    ...project_identity(project),
    meta: `${pluralized(domains.length, 'domain')} · ${pluralized(roles.length, 'role')}`,
    active: active_records.length.toLocaleString('en-ZA'),
    composition: domain_composition(domains),
    sla_ok: `${sla}%`,
    risk: risk_payload(risk)
  };
}

export function scopes_from_role_groups({ groups = [], roles = [], users = [], records = [] } = {}) {
  const normalized_roles = roles.map(normalize_role).filter((role) => role.id && role.name);
  const normalized_users = users.map(normalize_user).filter((user) => user.id);

  return groups
    .map(normalize_role_group)
    .filter((group) => group.id && group.name)
    .map((group) => scope_from_role_group(group, normalized_roles, normalized_users, records));
}

function normalize_role_group(group = {}) {
  return {
    id: Number(group.id || group.group_id),
    name: text(group.name || group.group_name || 'Unnamed group'),
    description: text(group.description)
  };
}

function normalize_role(role = {}) {
  return {
    id: Number(role.id || role.role_id),
    name: text(role.role || role.role_name || role.name),
    group_id: Number(role.group_id || role.group?.id || role.group),
    group_name: text(role.group_name || role.group?.name),
    entity_types: role_entity_types(role)
  };
}

function normalize_user(user = {}) {
  return {
    id: Number(user.user_id || user.id || user.user?.id),
    role_ids: role_ids_from_user(user),
    role_names: role_names_from_user(user)
  };
}

function scope_from_role_group(group, roles, users, records) {
  const group_roles = roles.filter((role) =>
    Number(role.group_id) === Number(group.id) || role.group_name === group.name
  );
  const group_role_ids = new Set(group_roles.map((role) => Number(role.id)));
  const group_role_names = new Set(group_roles.map((role) => normalized(role.name)));
  const group_users = users.filter((user) => user_in_group(user, group_role_ids, group_role_names));
  const group_records = records
    .filter(is_active_case)
    .filter((record) => record_matches_group(record, group_role_ids, group_role_names, group_users));
  const at_risk_count = group_records.filter(is_sla_at_risk).length;
  const high_risk_count = group_records.filter(is_high_risk).length;
  const risk = risk_label(group_records.length, high_risk_count);
  const sla = sla_percentage(group_records.length, at_risk_count);
  const entity_types = unique_values(group_roles.flatMap((role) => to_array(role.entity_types).map(text)));

  return {
    ...group,
    meta: `${pluralized(group_roles.length, 'role')} · ${pluralized(group_users.length, 'person')}`,
    active: group_records.length.toLocaleString('en-ZA'),
    composition: entity_type_composition(entity_types),
    sla_ok: `${sla}%`,
    risk: risk_payload(risk)
  };
}

function role_entity_types(role = {}) {
  return unique_values([
    role.entity_type_name,
    role.entity_type_label,
    role.domain_name,
    role.domain,
    ...metadata_entity_types(role.metadata)
  ].map(text));
}

function metadata_entity_types(metadata = {}) {
  return [
    metadata?.entity_type_name,
    metadata?.entity_type_label,
    metadata?.domain_name,
    metadata?.domain,
    ...to_array(metadata?.entity_types).flatMap(entity_type_values),
    ...to_array(metadata?.domains).flatMap(entity_type_values)
  ];
}

function entity_type_values(value = {}) {
  if (typeof value === 'string') return [value];
  return [
    value?.entity_type_name,
    value?.entity_type_label,
    value?.domain_name,
    value?.domain,
    value?.name,
    value?.label
  ];
}

function user_in_group(user, role_ids, role_names) {
  return user.role_ids.some((role_id) => role_ids.has(Number(role_id)))
    || user.role_names.some((role_name) => role_names.has(normalized(role_name)));
}

function record_matches_group(record, role_ids, role_names, users) {
  const user_ids = new Set(users.map((user) => Number(user.id)));
  return role_ids_from_record(record).some((role_id) => role_ids.has(Number(role_id)))
    || role_names_from_record(record).some((role_name) => role_names.has(normalized(role_name)))
    || user_ids_from_record(record).some((user_id) => user_ids.has(Number(user_id)));
}

function role_ids_from_user(user = {}) {
  return unique_number_values([
    user.role_id,
    user.user?.role_id,
    user.role?.id,
    user.profile_role?.role?.id,
    ...to_array(user.roles).map((role) => role?.id || role?.role_id),
    ...to_array(user.user_roles).map((assignment) => assignment?.role?.id || assignment?.role_id),
    ...to_array(user.profile_roles).map((assignment) => assignment?.role?.id || assignment?.role_id)
  ]);
}

function role_names_from_user(user = {}) {
  return unique_values([
    user.role_name,
    user.user?.role_name,
    user.role?.role,
    user.role?.name,
    user.profile_role?.role?.role,
    ...to_array(user.roles).map((role) => role?.role || role?.name),
    ...to_array(user.user_roles).map((assignment) => assignment?.role?.role || assignment?.role_name),
    ...to_array(user.profile_roles).map((assignment) => assignment?.role?.role || assignment?.role_name)
  ].map(text));
}

function role_ids_from_record(record = {}) {
  return unique_number_values([
    record.role_id,
    record.owner_role_id,
    record.assigned_role_id,
    record.assigned_profile_role?.role?.id,
    ...assignment_values(record).map((assignment) =>
      assignment?.role_id || assignment?.assigned_profile_role?.role?.id || assignment?.role?.id
    )
  ]);
}

function role_names_from_record(record = {}) {
  return unique_values([
    record.role_name,
    record.owner_role,
    record.assigned_role,
    record.assigned_profile_role?.role?.role,
    ...assignment_values(record).map((assignment) =>
      assignment?.role_name || assignment?.role || assignment?.assigned_role || assignment?.assigned_profile_role?.role?.role
    )
  ].map(text));
}

function user_ids_from_record(record = {}) {
  return unique_number_values([
    record.user_id,
    record.owner_id,
    record.assigned_user_id,
    record.assigned_to?.id,
    ...assignment_values(record).map((assignment) =>
      assignment?.user_id || assignment?.assigned_to?.id || assignment?.assigned_to_id
    )
  ]);
}

function assignment_values(record = {}) {
  return [
    ...to_array(record.assigned_users),
    ...to_array(record.assignments),
    ...to_array(record.assigned_panel_members),
    ...to_array(record.application_assigned_users)
  ];
}

function unique_number_values(values = []) {
  return [...new Set(values.map(Number).filter((value) => Number.isFinite(value) && value > 0))];
}

export function portfolio_domains(active_cases) {
  const counts = new Map();
  active_cases.forEach((record) => {
    const name = domain_name(record) || 'Other';
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const total = active_cases.length || 1;
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .map(([name, active], index) => ({
      name,
      active,
      percentage: (active / total) * 100,
      color: PORTFOLIO_COLORS[index % PORTFOLIO_COLORS.length]
    }));
}
